import React, { useState, useEffect } from 'react'
import { Search, Plus, Edit, Trash2, Eye, RefreshCw, ChevronDown, CheckCircle, XCircle, AlertCircle, X, Calendar, Filter } from 'lucide-react'
import { employeeService } from '../../services/employeeService'
import InlineLoader from '../../components/ui/InlineLoader'
import AddEmployeeModal from '../../components/modals/AddEmployeeModal'
import EditEmployeeModal from '../../components/modals/EditEmployeeModal'
import ViewEmployeeModal from '../../components/modals/ViewEmployeeModal'
import DeleteEmployeeModal from '../../components/modals/DeleteEmployeeModal'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-2 ${
    type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
    <span>{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2">
      <X className="h-4 w-4" />
    </button>
  </div>
)

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [jobFilter, setJobFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedEmployees, setSelectedEmployees] = useState([])
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [toastMessage, setToastMessage] = useState(null)

  // Helper function to safely get property value
  const safeGet = (obj, path, defaultValue = '') => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToastMessage({ message, type })
    setTimeout(() => setToastMessage(null), 3000)
  }

  useEffect(() => {
    fetchEmployees()
  }, [])

  const fetchEmployees = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await employeeService.getAllEmployees()
      if (response.success) {
        setEmployees(response.data.employees || [])
      } else {
        setError('Failed to fetch employees')
      }
    } catch (error) {
      console.error('Error fetching employees:', error)
      setError(error.message || 'Failed to load employees')
      setEmployees([])
    } finally {
      setLoading(false)
    }
  }

  const handleCreateEmployee = async (employeeData) => {
    try {
      const response = await employeeService.createEmployee(employeeData)
      if (response.success) {
        showToast('Employee created successfully')
        fetchEmployees()
        setShowAddModal(false)
      } else {
        showToast(response.message || 'Failed to create employee', 'error')
      }
      return response // Return the response for the modal to handle
    } catch (error) {
      console.error('Error creating employee:', error)
      showToast('Error creating employee', 'error')
      return { success: false, message: error.message || 'Error creating employee' } // Return error response
    }
  }

  const handleEditEmployee = async (employeeData) => {
    try {
      const response = await employeeService.updateEmployee(selectedEmployee._id, employeeData)
      if (response.success) {
        showToast('Employee updated successfully')
        fetchEmployees()
        setShowEditModal(false)
        setSelectedEmployee(null)
      } else {
        showToast(response.message || 'Failed to update employee', 'error')
      }
      return response // Return the response for the modal to handle
    } catch (error) {
      console.error('Error updating employee:', error)
      showToast('Error updating employee', 'error')
      return { success: false, message: error.message || 'Error updating employee' } // Return error response
    }
  }

  const handleDeleteEmployee = async () => {
    try {
      const response = await employeeService.deleteEmployee(selectedEmployee._id)
      
      if (response.success) {
        showToast('Employee deleted successfully')
        fetchEmployees()
        setShowDeleteModal(false)
        setSelectedEmployee(null)
      } else {
        showToast(response.message || 'Failed to delete employee', 'error')
      }
    } catch (error) {
      console.error('Error deleting employee:', error)
      showToast('Error deleting employee', 'error')
    }
  }

  const openViewModal = (employee) => {
    setSelectedEmployee(employee)
    setShowViewModal(true)
  }

  const openEditModal = (employee) => {
    setSelectedEmployee(employee)
    setShowEditModal(true)
  }

  const openDeleteModal = (employee) => {
    setSelectedEmployee(employee)
    setShowDeleteModal(true)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setJobFilter('all')
    setStatusFilter('all')
  }

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    )
  }

  const toggleSelectAll = () => {
    if (selectedEmployees.length === filteredEmployees.length) {
      setSelectedEmployees([])
    } else {
      setSelectedEmployees(filteredEmployees.map(emp => emp._id))
    }
  }

  // Create unique lists for filter dropdowns
  const uniqueJobs = [...new Set(employees.map(emp => safeGet(emp, 'job.name', '')).filter(Boolean))];

    const filteredEmployees = employees.filter(employee => {
    const matchesSearch = !searchTerm || 
      safeGet(employee, 'fullName', '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      safeGet(employee, 'contactNumber', '').toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesJob = jobFilter === 'all' || safeGet(employee, 'job.name') === jobFilter
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && employee.isActive) ||
      (statusFilter === 'inactive' && !employee.isActive)

    return matchesSearch && matchesJob && matchesStatus
  })

  // Loading state
  if (loading && employees.length === 0) {
    return (
      <InlineLoader text="Loading employees..." />
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Employee Management</h1>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={fetchEmployees}
                className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Refresh</span>
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Add Employee</span>
              </button>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-12">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading employees</h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <button
                onClick={fetchEmployees}
                className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors mx-auto"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Try Again</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Toast Notification */}
        {toastMessage && (
          <Toast
            message={toastMessage.message}
            type={toastMessage.type}
            onClose={() => setToastMessage(null)}
          />
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Employee Management</h1>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={fetchEmployees}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Add Employee</span>
            </button>
          </div>
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          {/* Filter Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filter Employees</h2>
            </div>
            {(searchTerm || jobFilter !== 'all' || statusFilter !== 'all') && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-orange-500 border border-orange-500 rounded-lg hover:bg-orange-50 transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by name or mobile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
              />
            </div>

            {/* Employee Type Filter */}
            <div>
              <select
                value={jobFilter}
                onChange={(e) => setJobFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="all">All Job Types</option>
                {uniqueJobs.map(job => (
                  <option key={job} value={job}>{job}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="text-gray-600">
            Showing {filteredEmployees.length} of {employees.length} employees
          </div>
        </div>

        {/* Employee List */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-4 border-b">
            <h3 className="text-lg font-medium text-gray-900">
              Employee List({filteredEmployees.length})
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {/* <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedEmployees.length === filteredEmployees.length && filteredEmployees.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                  </th> */}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Full name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile Number</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Branch</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                      {searchTerm || jobFilter !== 'all' || statusFilter !== 'all' 
                        ? 'No employees found matching your criteria' 
                        : 'No employees found'
                      }
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map((employee) => (
                    <tr key={employee._id} className="hover:bg-gray-50">
                      {/* <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedEmployees.includes(employee._id)}
                          onChange={() => toggleEmployeeSelection(employee._id)}
                          className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                      </td> */}
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {employee._id.slice(-8).toUpperCase()}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {employee.fullName}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {employee.contactNumber}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {safeGet(employee, 'job.name', 'N/A')}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {safeGet(employee, 'branch.name', 'N/A')}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            employee.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {employee.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openViewModal(employee)}
                            className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                            title="View Employee"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openEditModal(employee)}
                            className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                            title="Edit Employee"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(employee)}
                            className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                            title="Delete Employee"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modals */}
        <AddEmployeeModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleCreateEmployee}
        />

        <EditEmployeeModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false)
            setSelectedEmployee(null)
          }}
          onSubmit={handleEditEmployee}
          employee={selectedEmployee}
        />

        <ViewEmployeeModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false)
            setSelectedEmployee(null)
          }}
          employee={selectedEmployee}
        />

        <DeleteEmployeeModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false)
            setSelectedEmployee(null)
          }}
          onConfirm={handleDeleteEmployee}
          employee={selectedEmployee}
        />
      </div>
    </div>
  )
}

export default EmployeeManagement
